/**
 * Lazy loader for the Boundless chunk (boundless.js) — the daily-loader
 * pattern: the import URL is computed at runtime from the widget
 * script's own directory so esbuild leaves the `import()` native. The
 * chunk loads only when the served config says
 * `voice.presentation === 'boundless'` (anam-only, server-gated), so
 * chrome deploys never pay for the WebGL keyer or the stage.
 */

import type { mountBoundless } from './boundless/stage';
import type { startKeyer } from './boundless/keyer';

export type BoundlessModule = {
  mountBoundless: typeof mountBoundless;
  /** Optional: chunks built before 2026-09-04 don't export it. */
  startKeyer?: typeof startKeyer;
};

let chunkBase: string | null = null;
let cached: Promise<BoundlessModule> | null = null;

/** Set once at boot from the app.js directory (same base as daily/tours). */
export function setBoundlessChunkBase(base: string): void {
  chunkBase = base.replace(/\/$/, '');
}

export function loadBoundless(): Promise<BoundlessModule> {
  if (!cached) {
    if (!chunkBase) {
      return Promise.reject(
        new Error('[showpilot] boundless chunk base not configured before mount'),
      );
    }
    const url = `${chunkBase}/boundless.js`;
    cached = import(/* @vite-ignore */ url)
      .then((m: Partial<BoundlessModule>) => {
        if (typeof m.mountBoundless !== 'function') {
          throw new Error('[showpilot] boundless chunk loaded but module shape is wrong');
        }
        return m as BoundlessModule;
      })
      .catch((err: unknown) => {
        cached = null; // a later mount retries after a transient failure
        throw err;
      });
  }
  return cached;
}

/** Test hook — reset module state between cases. */
export function resetBoundlessLoaderForTests(): void {
  chunkBase = null;
  cached = null;
}
